/**
 * IndustryFinancialBenchmarks — Financial analytics panel for the Industry Research tab.
 *
 * Sub-sections:
 *   overview  → KPI metric cards + Competitor Financial Comparison Table
 *   trends    → Revenue + EBITDA margin + EBITDA absolute + Net Profit (with inline AI insights)
 *   capital   → EV + EV/EBITDA + ROIC + WACC + Invested Capital + D/E + capital snapshot (with inline AI insights)
 */

import { useMemo, useState } from 'react';
import { AlertCircle, BarChart2, TrendingUp, Landmark } from 'lucide-react';
import { useIndustryBenchmarks } from '../../lib/api/industryResearchApi';
import IndustryBenchmarkMetricCards from './IndustryBenchmarkMetricCards';
import IndustryCompetitorFinancialTable from './IndustryCompetitorFinancialTable';
import InlineInsight from './InlineInsight';
import IndustryRevenueComparisonChart from './charts/IndustryRevenueComparisonChart';
import IndustryMarginComparisonChart from './charts/IndustryMarginComparisonChart';
import IndustryEBITDAComparisonChart from './charts/IndustryEBITDAComparisonChart';
import IndustryEVComparisonChart from './charts/IndustryEVComparisonChart';
import IndustryEVMultipleComparisonChart from './charts/IndustryEVMultipleComparisonChart';
import IndustryROICTrendChart from './charts/IndustryROICTrendChart';
import IndustryLatestYearCapitalChart from './charts/IndustryLatestYearCapitalChart';
import IndustryNetProfitComparisonChart from './charts/IndustryNetProfitComparisonChart';
import IndustryWACCComparisonChart from './charts/IndustryWACCComparisonChart';
import IndustryInvestedCapitalChart from './charts/IndustryInvestedCapitalChart';
import IndustryDebtEquityChart from './charts/IndustryDebtEquityChart';
import MetricsComparisonSection from '../financial/sections/MetricsComparisonSection';
import CompetitorEVChart from '../financial/charts/CompetitorEVChart';
import CompetitorCAGRChart from '../financial/charts/CompetitorCAGRChart';
import CompetitorInvestedCapitalChart from '../financial/charts/CompetitorInvestedCapitalChart';
import EVBreakdownPercentageChart from '../financial/charts/EVBreakdownPercentageChart';
import EVBreakdownChart from '../financial/charts/EVBreakdownChart';
import DebtEquityChart from '../financial/charts/DebtEquityChart';
import WACCTrendChart from '../financial/charts/WACCTrendChart';
import ROICTrendChartWithToggle from '../financial/charts/ROICTrendChartWithToggle';
import EBITDAMarginTrendChartWithToggle from '../financial/charts/EBITDAMarginTrendChartWithToggle';
import type { RegionConfig, UnifiedFinancialRecord } from '../../types/financial';
import type { CompanyFinancialSummary, CompanyTimeSeries } from '../../types/industryResearch';

type Section = 'overview' | 'ev_ebitda' | 'revenue';

const SECTION_TABS: { key: Section; label: string; icon: React.ReactNode }[] = [
  { key: 'overview', label: 'Overview', icon: <BarChart2 className="h-3.5 w-3.5" /> },
  { key: 'ev_ebitda', label: 'EV Insights', icon: <Landmark className="h-3.5 w-3.5" /> },
  { key: 'revenue', label: 'Revenue & Capital', icon: <TrendingUp className="h-3.5 w-3.5" /> },
];

function SectionDivider({ title }: { title: string }) {
  return (
    <div className="flex items-center gap-3 pt-4 pb-1">
      <div className="h-px flex-1 bg-gradient-to-r from-transparent via-neutral-200 to-neutral-200" />
      <span className="text-xs font-bold text-neutral-400 uppercase tracking-widest px-1 shrink-0">
        {title}
      </span>
      <div className="h-px flex-1 bg-gradient-to-l from-transparent via-neutral-200 to-neutral-200" />
    </div>
  );
}

const LEGAL_SUFFIX_REGEX =
  /\b(ltd|limited|inc|incorporated|corp|corporation|plc|llc|pvt|private)\b/gi;

function normalizeCompanyName(name: string): string {
  return name
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[.,'’`()-]/g, ' ')
    .replace(LEGAL_SUFFIX_REGEX, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function isPresent<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

function choosePresent<T>(...values: Array<T | null | undefined>): T | undefined {
  return values.find(isPresent);
}

function companySummaryScore(c: CompanyFinancialSummary): number {
  const metricCount = [
    c.latest_revenue,
    c.latest_ebitda,
    c.latest_ebitda_margin,
    c.latest_net_profit,
    c.latest_roic,
    c.latest_ev_ebitda,
    c.latest_ev,
  ].filter(isPresent).length;

  return (
    (c.has_financial_data ? 1_000 : 0) +
    ((c.years_of_data ?? 0) * 10) +
    metricCount
  );
}

function mergeCompanySummaries(
  summaries: CompanyFinancialSummary[]
): CompanyFinancialSummary[] {
  const mergedByKey = new Map<string, CompanyFinancialSummary>();

  for (const row of summaries) {
    const key = normalizeCompanyName(row.company_name);
    const existing = mergedByKey.get(key);
    if (!existing) {
      mergedByKey.set(key, { ...row });
      continue;
    }

    const [preferred, fallback] =
      companySummaryScore(row) >= companySummaryScore(existing)
        ? [row, existing]
        : [existing, row];

    const mergedName =
      preferred.has_financial_data && preferred.company_name
        ? preferred.company_name
        : preferred.company_name.length >= fallback.company_name.length
          ? preferred.company_name
          : fallback.company_name;

    mergedByKey.set(key, {
      ...fallback,
      ...preferred,
      company_name: mergedName,
      market_share: choosePresent(preferred.market_share, fallback.market_share),
      ticker: choosePresent(preferred.ticker, fallback.ticker),
      latest_year: choosePresent(preferred.latest_year, fallback.latest_year),
      latest_revenue: choosePresent(preferred.latest_revenue, fallback.latest_revenue),
      latest_ebitda: choosePresent(preferred.latest_ebitda, fallback.latest_ebitda),
      latest_ebitda_margin: choosePresent(preferred.latest_ebitda_margin, fallback.latest_ebitda_margin),
      latest_ebit: choosePresent(preferred.latest_ebit, fallback.latest_ebit),
      latest_net_profit: choosePresent(preferred.latest_net_profit, fallback.latest_net_profit),
      latest_roic: choosePresent(preferred.latest_roic, fallback.latest_roic),
      latest_wacc: choosePresent(preferred.latest_wacc, fallback.latest_wacc),
      latest_ev_ebitda: choosePresent(preferred.latest_ev_ebitda, fallback.latest_ev_ebitda),
      latest_debt_equity: choosePresent(preferred.latest_debt_equity, fallback.latest_debt_equity),
      latest_ev: choosePresent(preferred.latest_ev, fallback.latest_ev),
      latest_net_debt: choosePresent(preferred.latest_net_debt, fallback.latest_net_debt),
      latest_market_cap: choosePresent(preferred.latest_market_cap, fallback.latest_market_cap),
      latest_invested_capital: choosePresent(
        preferred.latest_invested_capital,
        fallback.latest_invested_capital
      ),
      above_median_revenue: choosePresent(preferred.above_median_revenue, fallback.above_median_revenue),
      above_median_ebitda_margin: choosePresent(
        preferred.above_median_ebitda_margin,
        fallback.above_median_ebitda_margin
      ),
      above_median_roic: choosePresent(preferred.above_median_roic, fallback.above_median_roic),
      above_median_ev_ebitda: choosePresent(
        preferred.above_median_ev_ebitda,
        fallback.above_median_ev_ebitda
      ),
      years_of_data: Math.max(preferred.years_of_data ?? 0, fallback.years_of_data ?? 0),
      has_financial_data: preferred.has_financial_data || fallback.has_financial_data,
    });
  }

  return Array.from(mergedByKey.values());
}

function mergeCompanyTimeSeriesRows(rows: CompanyTimeSeries[]): CompanyTimeSeries[] {
  const byKey = new Map<string, CompanyTimeSeries>();

  for (const row of rows) {
    const key = normalizeCompanyName(row.company_name);
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, row);
      continue;
    }

    const keepCurrent =
      row.data.length > existing.data.length ||
      (row.data.length === existing.data.length &&
        row.company_name.length > existing.company_name.length);

    if (keepCurrent) byKey.set(key, row);
  }

  return Array.from(byKey.values());
}

function FocalCompanySelector({
  value,
  options,
  onChange,
  description,
}: {
  value: string;
  options: string[];
  onChange: (v: string) => void;
  description?: string;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-neutral-200 bg-white p-3 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-xs text-neutral-500">{description ?? 'Focal company drives the peer-comparison charts below.'}</p>
      <div className="flex items-center gap-2">
        <label className="text-xs font-medium text-neutral-600">Focal company</label>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 min-w-[200px] rounded-lg border border-neutral-200 bg-white px-2.5 text-xs text-neutral-700 focus:outline-none focus:ring-1 focus:ring-primary-400"
        >
          {options.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function buildPeerInputs(
  series: CompanyTimeSeries[],
  focalName: string,
): {
  data: UnifiedFinancialRecord[];
  competitorData: Record<string, UnifiedFinancialRecord[]>;
} {
  const focal = series.find((s) => s.company_name === focalName);
  const peers = series.filter((s) => s.company_name !== focalName);
  return {
    data: focal?.data ?? [],
    competitorData: Object.fromEntries(peers.map((p) => [p.company_name, p.data])),
  };
}

interface Props {
  industryId: string;
}

export default function IndustryFinancialBenchmarks({ industryId }: Props) {
  const [activeSection, setActiveSection] = useState<Section>('overview');
  const [selectedComparisonCompany, setSelectedComparisonCompany] = useState<string>('');
  const { data, isLoading, error } = useIndustryBenchmarks(industryId);

  const normalizedData = useMemo(() => {
    if (!data) {
      return {
        companyDetails: [] as CompanyFinancialSummary[],
        companiesTimeSeries: [] as CompanyTimeSeries[],
        companiesIncluded: [] as string[],
      };
    }

    const companyDetails = mergeCompanySummaries(data.company_details ?? []);
    const companiesTimeSeries = mergeCompanyTimeSeriesRows(data.companies_time_series ?? []);

    const preferredNameByKey = new Map<string, string>();
    companyDetails.forEach((co) => {
      preferredNameByKey.set(normalizeCompanyName(co.company_name), co.company_name);
    });
    companiesTimeSeries.forEach((co) => {
      const key = normalizeCompanyName(co.company_name);
      if (!preferredNameByKey.has(key)) preferredNameByKey.set(key, co.company_name);
    });

    const seenIncluded = new Set<string>();
    const companiesIncluded: string[] = [];
    for (const originalName of data.companies_included ?? []) {
      const key = normalizeCompanyName(originalName);
      if (seenIncluded.has(key)) continue;
      seenIncluded.add(key);
      companiesIncluded.push(preferredNameByKey.get(key) ?? originalName);
    }

    return {
      companyDetails,
      companiesTimeSeries,
      companiesIncluded,
    };
  }, [data]);

  const companyOptions = normalizedData.companiesIncluded;

  const defaultComparisonCompany = useMemo(() => {
    if (companyOptions.length === 0) return '';

    const leaderByShare = [...normalizedData.companyDetails]
      .filter((c) => c.company_name && companyOptions.includes(c.company_name))
      .sort((a, b) => (b.market_share ?? -1) - (a.market_share ?? -1))[0]?.company_name;

    return leaderByShare ?? companyOptions[0];
  }, [companyOptions, normalizedData.companyDetails]);

  const focalComparisonCompany = useMemo(() => {
    if (companyOptions.length === 0) return '';
    if (selectedComparisonCompany && companyOptions.includes(selectedComparisonCompany)) {
      return selectedComparisonCompany;
    }
    return defaultComparisonCompany;
  }, [companyOptions, defaultComparisonCompany, selectedComparisonCompany]);

  const focalComparisonPeers = useMemo(
    () => companyOptions.filter((name) => name !== focalComparisonCompany),
    [companyOptions, focalComparisonCompany]
  );

  if (isLoading) {
    return (
      <div className="space-y-5 animate-pulse">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="space-y-1.5">
            <div className="h-4 bg-neutral-200 rounded w-44" />
            <div className="h-3 bg-neutral-100 rounded w-56" />
          </div>
          <div className="h-6 bg-neutral-100 rounded-full w-16" />
        </div>

        {/* Section pill tabs */}
        <div className="flex gap-1.5 bg-neutral-100 p-1 rounded-xl w-fit">
          {['w-20', 'w-32', 'w-28'].map((w, i) => (
            <div key={i} className={`h-7 bg-neutral-200 rounded-lg ${w}`} />
          ))}
        </div>

        {/* Metric cards row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
              <div className="h-3 bg-neutral-100 rounded w-3/5" />
              <div className="h-6 bg-neutral-200 rounded w-2/3" />
              <div className="h-2.5 bg-neutral-100 rounded w-4/5" />
            </div>
          ))}
        </div>

        {/* Competitor table */}
        <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-6 gap-4 px-4 py-3 border-b border-neutral-100 bg-neutral-50">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-3 bg-neutral-200 rounded" />
            ))}
          </div>
          {/* Table rows */}
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="grid grid-cols-6 gap-4 px-4 py-3.5 border-b border-neutral-100 last:border-b-0"
            >
              <div className="h-3 bg-neutral-200 rounded w-4/5" />
              {Array.from({ length: 5 }).map((_, j) => (
                <div key={j} className="h-3 bg-neutral-100 rounded w-3/5 mx-auto" />
              ))}
            </div>
          ))}
        </div>

        {/* Section divider + chart placeholder */}
        <div className="flex items-center gap-3 pt-2">
          <div className="h-px flex-1 bg-neutral-200" />
          <div className="h-3 bg-neutral-200 rounded w-36" />
          <div className="h-px flex-1 bg-neutral-200" />
        </div>
        <div className="bg-white border border-neutral-200 rounded-xl p-4 h-40" />
      </div>
    );
  }

  if (error || !data) {
    const msg = (error as Error)?.message ?? 'Could not load financial benchmarks';
    return (
      <div className="flex items-center gap-3 p-5 bg-amber-50 border border-amber-200 rounded-xl text-amber-800">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <div>
          <p className="text-sm font-medium">Financial benchmarks unavailable</p>
          <p className="text-xs mt-0.5 text-amber-600">{msg}</p>
        </div>
      </div>
    );
  }

  if (!data.success || data.aggregated_data.length === 0) {
    return (
      <div className="flex items-center gap-3 p-5 bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-600">
        <AlertCircle className="h-4 w-4 shrink-0 text-neutral-400" />
        <div>
          <p className="text-sm font-medium text-neutral-700">No financial data available</p>
          <p className="text-xs mt-0.5">
            {data.message ??
              'No financial data has been processed for the companies in this industry yet. Process each company through Company Research to enable benchmarks.'}
          </p>
        </div>
      </div>
    );
  }

  const region = data.region as unknown as RegionConfig;

  const hasTimeSeries = normalizedData.companiesTimeSeries.length > 0;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-neutral-800">Financial Benchmarks</h2>
          <p className="text-xs text-neutral-400 mt-0.5">
            {data.companies_count} market leader{data.companies_count !== 1 ? 's' : ''} · {data.years_available} years
            {hasTimeSeries && ` · per-company breakdown`}
          </p>
        </div>
        <span className="text-xs text-neutral-400 bg-neutral-50 border border-neutral-200 px-2.5 py-1 rounded-full">
          {data.region.country}
        </span>
      </div>

      {/* Sub-section pill tabs */}
      <div className="flex gap-1.5 bg-neutral-100 p-1 rounded-xl w-fit">
        {SECTION_TABS.map(({ key, label, icon }) => (
          <button
            key={key}
            onClick={() => setActiveSection(key)}
            className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
              activeSection === key
                ? 'bg-white text-neutral-800 shadow-sm'
                : 'text-neutral-500 hover:text-neutral-700'
            }`}
          >
            {icon}
            {label}
          </button>
        ))}
      </div>

      {/* ── Overview ─────────────────────────────────────────────────────── */}
      {activeSection === 'overview' && data.latest_summary && (
        <div className="space-y-5">
          <IndustryBenchmarkMetricCards
            summary={data.latest_summary}
            region={region}
            companiesCount={data.companies_count}
          />
          <IndustryCompetitorFinancialTable
            companies={normalizedData.companyDetails}
            medians={data.latest_summary}
            region={region}
          />

          {companyOptions.length > 0 && (
            <>
              <SectionDivider title="Financial Metrics Comparison" />
              <FocalCompanySelector
                value={focalComparisonCompany}
                options={companyOptions}
                onChange={setSelectedComparisonCompany}
                description="Company-vs-peers view inside this industry benchmark."
              />
              <MetricsComparisonSection
                companyName={focalComparisonCompany}
                competitors={focalComparisonPeers}
              />
            </>
          )}
        </div>
      )}

      {/* ── EV/EBITDA ─────────────────────────────────────────────────────── */}
      {/* Mirrors the Company Research "EV Insights" tab:                     */}
      {/*   Enterprise Value, EV/EBITDA multiple, EBITDA & margin analysis,   */}
      {/*   plus multi-company peer comparison charts (#1, #2, #7, #9).      */}
      {activeSection === 'ev_ebitda' && hasTimeSeries && (() => {
        const peerInputs = buildPeerInputs(
          normalizedData.companiesTimeSeries,
          focalComparisonCompany,
        );
        return (
          <div className="space-y-5">
            <SectionDivider title="Enterprise Value" />
            <IndustryEVComparisonChart
              companies={normalizedData.companiesTimeSeries}
              region={region}
            />
            <IndustryEVMultipleComparisonChart
              companies={normalizedData.companiesTimeSeries}
              medianData={data.aggregated_data}
              region={region}
            />
            <InlineInsight industryId={industryId} metric="ev" />

            <SectionDivider title="EBITDA & Margins" />
            <IndustryEBITDAComparisonChart
              companies={normalizedData.companiesTimeSeries}
              region={region}
            />
            <IndustryMarginComparisonChart
              companies={normalizedData.companiesTimeSeries}
              medianData={data.aggregated_data}
              region={region}
            />
            <IndustryNetProfitComparisonChart
              companies={normalizedData.companiesTimeSeries}
              region={region}
            />
            <InlineInsight industryId={industryId} metric="ebitda" />

            {companyOptions.length > 1 && peerInputs.data.length > 0 && (
              <>
                <SectionDivider title="Multi-Company Comparison" />
                <FocalCompanySelector
                  value={focalComparisonCompany}
                  options={companyOptions}
                  onChange={setSelectedComparisonCompany}
                />
                {/* #1 Competitor EV/EBITDA Comparison */}
                <CompetitorEVChart
                  data={peerInputs.data}
                  region={region}
                  companyName={focalComparisonCompany}
                  competitorData={peerInputs.competitorData}
                />
                {/* #9 Enterprise Value Breakdown: Current vs Future Growth */}
                <EVBreakdownChart data={peerInputs.data} region={region} />
                {/* #2 EV Composition Analysis (peer mode) */}
                <EVBreakdownPercentageChart
                  data={peerInputs.data}
                  region={region}
                  companyName={focalComparisonCompany}
                  competitorData={peerInputs.competitorData}
                />
                {/* #7 10-Year EBITDA Margin Trend — Multi-Company Comparison */}
                <EBITDAMarginTrendChartWithToggle
                  data={peerInputs.data}
                  region={region}
                  companyName={focalComparisonCompany}
                  competitorData={peerInputs.competitorData}
                />
              </>
            )}
          </div>
        );
      })()}
      {activeSection === 'ev_ebitda' && !hasTimeSeries && (
        <p className="text-xs text-neutral-400 italic py-6 text-center">
          No per-company time series available for this industry.
        </p>
      )}

      {/* ── Revenue ──────────────────────────────────────────────────────── */}
      {/* Mirrors the Company Research "Revenue & Capital" tab:               */}
      {/*   Revenue, Capital Efficiency (ROIC / WACC), Capital Structure      */}
      {/*   (Invested Capital / Debt-to-Equity), plus multi-company peer      */}
      {/*   comparison charts (#3, #4, #5, #6, #8).                          */}
      {activeSection === 'revenue' && hasTimeSeries && (() => {
        const peerInputs = buildPeerInputs(
          normalizedData.companiesTimeSeries,
          focalComparisonCompany,
        );
        return (
          <div className="space-y-5">
            <SectionDivider title="Revenue" />
            <IndustryRevenueComparisonChart
              companies={normalizedData.companiesTimeSeries}
              medianData={data.aggregated_data}
              region={region}
            />
            <InlineInsight industryId={industryId} metric="revenue" />

            <SectionDivider title="Capital Efficiency" />
            <IndustryROICTrendChart
              companies={normalizedData.companiesTimeSeries}
              medianData={data.aggregated_data}
              region={region}
            />
            <IndustryWACCComparisonChart
              companies={normalizedData.companiesTimeSeries}
              medianData={data.aggregated_data}
              region={region}
            />
            <InlineInsight industryId={industryId} metric="roic" />

            <SectionDivider title="Capital Structure" />
            <IndustryInvestedCapitalChart
              companies={normalizedData.companiesTimeSeries}
              region={region}
            />
            <IndustryDebtEquityChart
              companies={normalizedData.companiesTimeSeries}
              medianData={data.aggregated_data}
              region={region}
            />
            {data.latest_summary && (
              <IndustryLatestYearCapitalChart
                companies={normalizedData.companyDetails}
                latestMedian={data.latest_summary}
              />
            )}

            {companyOptions.length > 1 && peerInputs.data.length > 0 && (
              <>
                <SectionDivider title="Multi-Company Comparison" />
                <FocalCompanySelector
                  value={focalComparisonCompany}
                  options={companyOptions}
                  onChange={setSelectedComparisonCompany}
                />
                {/* #3 Competitor Revenue CAGR Comparison */}
                <CompetitorCAGRChart
                  data={peerInputs.data}
                  companyName={focalComparisonCompany}
                  competitorData={peerInputs.competitorData}
                  region={region}
                  years={10}
                />
                {/* #4 Competitor Invested Capital CAGR Comparison */}
                <CompetitorInvestedCapitalChart
                  data={peerInputs.data}
                  companyName={focalComparisonCompany}
                  competitorData={peerInputs.competitorData}
                  region={region}
                  years={10}
                />
                {/* #8 10-Year ROIC Trend — Multi-Company Comparison */}
                <ROICTrendChartWithToggle
                  data={peerInputs.data}
                  region={region}
                  companyName={focalComparisonCompany}
                  competitorData={peerInputs.competitorData}
                />
                {/* #6 Competitor WACC Comparison */}
                <WACCTrendChart
                  data={peerInputs.data}
                  region={region}
                  companyName={focalComparisonCompany}
                  competitorData={peerInputs.competitorData}
                />
                {/* #5 Competitor Debt/Equity Comparison */}
                <DebtEquityChart
                  data={peerInputs.data}
                  region={region}
                  companyName={focalComparisonCompany}
                  competitorData={peerInputs.competitorData}
                />
              </>
            )}
          </div>
        );
      })()}
      {activeSection === 'revenue' && !hasTimeSeries && data.latest_summary && (
        <div className="space-y-5">
          <SectionDivider title="Capital Snapshot" />
          <IndustryLatestYearCapitalChart
            companies={normalizedData.companyDetails}
            latestMedian={data.latest_summary}
          />
        </div>
      )}
    </div>
  );
}
